import { useAuth } from '../auth/useAuth';
import './SimulationWatermark.css';

/**
 * A watermark over any projection that is being driven by a slider rather than by data.
 *
 * WHY THIS EXISTS. Both offering screens render a full waterfall — gross, fees, net
 * proceeds, capital returned, the 8% hurdle, the 60/40 split — from numbers a visitor
 * chose with a control. The output is formatted exactly like the settled figures shown
 * elsewhere in the product, so a screenshot of it is indistinguishable from a statement.
 * That screenshot is the risk: forwarded, it becomes a projection presented as a result,
 * and for a securities offering that is not a cosmetic problem.
 *
 * The watermark makes the screenshot carry its own disclaimer. It only appears once the
 * visitor has actually moved something away from its baseline, so the default view stays
 * clean and the mark means something specific when it does show.
 *
 * NON-INTERACTIVE BY CONSTRUCTION. pointer-events-none, so it cannot intercept a click on
 * the controls beneath it. A warning that blocked the thing it was warning about would be
 * removed within a week.
 *
 * The name is whoever is signed in, falling back to CONFIDENTIAL. On the public offering
 * page nobody is signed in, so it reads CONFIDENTIAL there — which is the correct label
 * for an anonymous visitor's own projection.
 */
export function SimulationWatermark({ active }: { active: boolean }) {
  const { user } = useAuth();
  if (!active) return null;

  // The User record carries firstName/lastName; there is no legalName field on it.
  // Composing one here keeps the fallback meaningful instead of always reading
  // CONFIDENTIAL for a signed-in admin.
  const legalName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

  return (
    <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center overflow-hidden bg-transparent opacity-10">
      <div className="rotate-[-30deg] text-6xl font-black text-red-500 uppercase tracking-widest text-center whitespace-nowrap">
        SIMULATION MODE <br /> {legalName || 'CONFIDENTIAL'}
      </div>
    </div>
  );
}
